import { h } from "@stencil/core";
import { IntersectionObserverHelper } from "../../../utils/IntersectionObserver.helper";
import { raf } from "../../../utils/helpers";
import { fetchSvg, iconCache, iconRequests } from "./utils";
/**
 * AXA Icon
 *
 * This icon component displays a specific icon which is dynamically loaded from a svg-file.
 * The icon svg's are not part of base code package, but will be dynamically loaded. The icon
 * will be loaded when element appears in the visible viewport and shown right after loading has
 * completed. So all you have to do is just add the icon-token to the name-attribute. The size
 * can be controlled size property or via css-style font-size. To override the theme-settings you
 * can also use the css-props --icon-size-{size}
 *
 * @element axa-icon
 * @csspart icon
 * @cssproperty --icon-color
 * @cssproperty --icon-size-{size}
 */
export class Icon {
    constructor() {
        /**
         * defines if the icon is lazy loaded or not (default: false)
         */
        this.lazyLoad = false;
        /**
         * sets alt text
         */
        this.alt = '';
        this._svg = '';
        this._loaded = false;
    }
    onAltChange() {
        this._setAlt();
    }
    /**
     * Handles changes to the `name` property
     */
    onNameChange() {
        this._loaded = false;
        void this.loadIcon();
    }
    lazyLoadChanged(newValue) {
        if (newValue !== undefined && !this._loaded) {
            this.handleLazyLoad();
        }
    }
    componentWillLoad() {
        this.handleLazyLoad();
    }
    async componentWillUpdate() {
        await this.loadIcon();
    }
    disconnectedCallback() {
        if (this.intersectionObserverHelper) {
            this.intersectionObserverHelper.disconnect();
            this.intersectionObserverHelper = undefined;
        }
    }
    handleLazyLoad() {
        if (this.intersectionObserverHelper) {
            this.intersectionObserverHelper.disconnect();
            this.intersectionObserverHelper = undefined;
        }
        if (!this.lazyLoad) {
            void this.loadIcon();
        }
        else {
            this.intersectionObserverHelper = new IntersectionObserverHelper(this.el, { threshold: 0 }, entry => {
                if (entry.isIntersecting) {
                    void this.loadIcon();
                }
            });
            this.intersectionObserverHelper.observe();
        }
    }
    _setAlt() {
        var _a;
        const icon = (_a = this.el.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('svg');
        if (!icon)
            return;
        // Remove any existing <title> element
        const existingTitle = icon.querySelector('title');
        if (existingTitle) {
            existingTitle.remove();
        }
        if (this.alt) {
            const title = document.createElementNS('http://www.w3.org/2000/svg', 'title');
            title.textContent = this.alt;
            icon.insertBefore(title, icon.firstChild);
            // Remove aria-hidden if present
            icon.removeAttribute('aria-hidden');
        }
        else {
            icon.setAttribute('aria-hidden', 'true');
        }
    }
    async loadIcon() {
        if (this._loaded) {
            return;
        }
        try {
            this._svg = await this.loadIconSvg();
            this._loaded = true;
            raf(() => this._setAlt());
        }
        catch (_a) {
            // Ignored and already logged in util method
        }
    }
    loadIconSvg() {
        const cachedIcon = iconCache.get(this.name);
        if (cachedIcon) {
            return cachedIcon;
        }
        const req = iconRequests.get(this.name);
        if (req) {
            return req;
        }
        return fetchSvg(this.name);
    }
    render() {
        return (h("i", { key: '8f8eade83cb89515c9d23b5a55ac9e29a5bb191b', part: "icon", class: {
                icon: true,
                [`icon--${this.size}`]: !!this.size,
            } }, h("div", { key: '5d588fcf3805ffcdc037ec18c6fcfb6504528f99', innerHTML: this._svg })));
    }
    static get is() { return "axa-icon"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["Icon.css"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["Icon.css"]
        };
    }
    static get assetsDirs() { return ["assets"]; }
    static get properties() {
        return {
            "name": {
                "type": "string",
                "attribute": "name",
                "mutable": false,
                "complexType": {
                    "original": "IconName",
                    "resolved": "\"common-functional-24\" | \"common-functional-account_balance\" | \"common-functional-add_circle_outline\" | \"common-functional-arrow\" | \"common-functional-arrow_back\" | \"common-functional-arrow_backward\" | \"common-functional-arrow_circle_left\" | \"common-functional-arrow_circle_right\" | \"common-functional-arrow_down\" | \"common-functional-arrow_forward\" | \"common-functional-arrow_forward_1\" | \"common-functional-arrow_left\" | \"common-functional-arrow_right\" | \"common-functional-arrow_right2\" | \"common-functional-attach\" | \"common-functional-audioplay\" | \"common-functional-auto_scan\" | \"common-functional-auto_scan_off\" | \"common-functional-bin\" | \"common-functional-burger_menu\" | \"common-functional-button_forward\" | \"common-functional-calculator\" | \"common-functional-calculator2\" | \"common-functional-calendar\" | \"common-functional-cancel_circle_outline\" | \"common-functional-car\" | \"common-functional-chair\" | \"common-functional-chat_1\" | \"common-functional-check\" | \"common-functional-check_circle\" | \"common-functional-check_circle_outline\" | \"common-functional-chevron_left\" | \"common-functional-chevron_right\" | \"common-functional-clock\" | \"common-functional-close\" | \"common-functional-close_menu\" | \"common-functional-collapse_more\" | \"common-functional-contact\" | \"common-functional-crop\" | \"common-functional-cross\" | \"common-functional-danger\" | \"common-functional-document\" | \"common-functional-document_multiple\" | \"common-functional-document_multiple_off\" | \"common-functional-document_scan\" | \"common-functional-dot\" | \"common-functional-down\" | \"common-functional-download\" | \"common-functional-edit\" | \"common-functional-email\" | \"common-functional-error\" | \"common-functional-expand_more\" | \"common-functional-eye\" | \"common-functional-facebook\" | \"common-functional-family\" | \"common-functional-fidelity\" | \"common-functional-filter_outline\" | \"common-functional-first_page\" | \"common-functional-flash\" | \"common-functional-flash_off\" | \"common-functional-gavel\" | \"common-functional-gear\" | \"common-functional-googleplus\" | \"common-functional-growth\" | \"common-functional-hand_leaf\" | \"common-functional-health_kit\" | \"common-functional-house\" | \"common-functional-inbox\" | \"common-functional-inbox_outline\" | \"common-functional-info\" | \"common-functional-instagram\" | \"common-functional-inventory\" | \"common-functional-inventory_outline\" | \"common-functional-kununu\" | \"common-functional-last_page\" | \"common-functional-linkedin\" | \"common-functional-list\" | \"common-functional-loader\" | \"common-functional-lock_login\" | \"common-functional-login_logout\" | \"common-functional-magnifier_glass\" | \"common-functional-man\" | \"common-functional-map\" | \"common-functional-minus\" | \"common-functional-mobile\" | \"common-functional-more_vert\" | \"common-functional-newspaper\" | \"common-functional-notifications\" | \"common-functional-padlock\" | \"common-functional-phone\" | \"common-functional-photo_camera\" | \"common-functional-pinlocator\" | \"common-functional-play\" | \"common-functional-plus\" | \"common-functional-pound\" | \"common-functional-print\" | \"common-functional-priority\" | \"common-functional-question_mark\" | \"common-functional-quotation\" | \"common-functional-remove_circle_outline\" | \"common-functional-reply\" | \"common-functional-reset\" | \"common-functional-rotate\" | \"common-functional-search_left\" | \"common-functional-search_right\" | \"common-functional-send\" | \"common-functional-send_outline\" | \"common-functional-service\" | \"common-functional-share\" | \"common-functional-shield\" | \"common-functional-shopping_cart\" | \"common-functional-star_rate\" | \"common-functional-swap_vert\" | \"common-functional-target\" | \"common-functional-tools\" | \"common-functional-tooltip\" | \"common-functional-twitter\" | \"common-functional-up\" | \"common-functional-upload\" | \"common-functional-videoplay\" | \"common-functional-visibility_off\" | \"common-functional-web\" | \"common-functional-wechat\" | \"common-functional-whatsapp\" | \"common-functional-xing\" | \"common-functional-yen\" | \"common-functional-youtube\" | \"common-illustrative-7\" | \"common-illustrative-chat\" | \"common-illustrative-accident\" | \"common-illustrative-accidents\" | \"common-illustrative-agentman\" | \"common-illustrative-agentwoman\" | \"common-illustrative-baby\" | \"common-illustrative-bank\" | \"common-illustrative-bankeuro\" | \"common-illustrative-billseuro\" | \"common-illustrative-breakdown\" | \"common-illustrative-breakpoints\" | \"common-illustrative-briefcase\" | \"common-illustrative-brokenglass\" | \"common-illustrative-calculator\" | \"common-illustrative-calendar\" | \"common-illustrative-cancellation\" | \"common-illustrative-car\" | \"common-illustrative-chatman\" | \"common-illustrative-chatwoman\" | \"common-illustrative-check\" | \"common-illustrative-checklist\" | \"common-illustrative-climate_event\" | \"common-illustrative-clock\" | \"common-illustrative-coinseuro\" | \"common-illustrative-collision_foreign_property\" | \"common-illustrative-contract\" | \"common-illustrative-creditcard\" | \"common-illustrative-criticalillness\" | \"common-illustrative-danger\" | \"common-illustrative-dental\" | \"common-illustrative-dumbell\" | \"common-illustrative-desktop\" | \"common-illustrative-document\" | \"common-illustrative-download\" | \"common-illustrative-earth\" | \"common-illustrative-elderlyman\" | \"common-illustrative-elderlywoman\" | \"common-illustrative-electriccar\" | \"common-illustrative-electric_damages\" | \"common-illustrative-email\" | \"common-illustrative-emergency\" | \"common-illustrative-employee\" | \"common-illustrative-facebook\" | \"common-illustrative-family1\" | \"common-illustrative-family2\" | \"common-illustrative-fidelity\" | \"common-illustrative-fire\" | \"common-illustrative-firehouse\" | \"common-illustrative-floodedhouse\" | \"common-illustrative-gear\" | \"common-illustrative-gender\" | \"common-illustrative-graduationhat\" | \"common-illustrative-green\" | \"common-illustrative-growth\" | \"common-illustrative-growthdollar\" | \"common-illustrative-growtheuro\" | \"common-illustrative-hail\" | \"common-illustrative-handcross\" | \"common-illustrative-handheart\" | \"common-illustrative-health_card\" | \"common-illustrative-healthkit\" | \"common-illustrative-heart\" | \"common-illustrative-help\" | \"common-illustrative-helpman\" | \"common-illustrative-helpwoman\" | \"common-illustrative-hospital\" | \"common-illustrative-hospitalbed\" | \"common-illustrative-house\" | \"common-illustrative-house_single\" | \"common-illustrative-inability_to_work\" | \"common-illustrative-inbox\" | \"common-illustrative-instagram\" | \"common-illustrative-keys\" | \"common-illustrative-kununu\" | \"common-illustrative-legal\" | \"common-illustrative-linkedin\" | \"common-illustrative-loginlogout\" | \"common-illustrative-magnifierglass\" | \"common-illustrative-magnifierglass_x\" | \"common-illustrative-man\" | \"common-illustrative-manwoman\" | \"common-illustrative-medal1st\" | \"common-illustrative-medications\" | \"common-illustrative-mindset\" | \"common-illustrative-minus\" | \"common-illustrative-mobile\" | \"common-illustrative-modularoffer\" | \"common-illustrative-more\" | \"common-illustrative-motorcycle\" | \"common-illustrative-newspaper\" | \"common-illustrative-note_pen\" | \"common-illustrative-padlock\" | \"common-illustrative-pen\" | \"common-illustrative-phone\" | \"common-illustrative-phone_recall\" | \"common-illustrative-piggybank\" | \"common-illustrative-pinlocator\" | \"common-illustrative-pinlocatormap\" | \"common-illustrative-plane\" | \"common-illustrative-plastercrutch\" | \"common-illustrative-play\" | \"common-illustrative-plus\" | \"common-illustrative-print\" | \"common-illustrative-questionmark\" | \"common-illustrative-realtime\" | \"common-illustrative-risk_protection\" | \"common-illustrative-rockingchair\" | \"common-illustrative-sailboat\" | \"common-illustrative-savings\" | \"common-illustrative-savingseuro\" | \"common-illustrative-securitycamera\" | \"common-illustrative-service\" | \"common-illustrative-share\" | \"common-illustrative-shield\" | \"common-illustrative-signpost\" | \"common-illustrative-sofa\" | \"common-illustrative-speechbubble\" | \"common-illustrative-stethoscope\" | \"common-illustrative-suitcase\" | \"common-illustrative-sunbed\" | \"common-illustrative-symboleuro\" | \"common-illustrative-tablet\" | \"common-illustrative-target\" | \"common-illustrative-taxi\" | \"common-illustrative-team\" | \"common-illustrative-theft\" | \"common-illustrative-timeline\" | \"common-illustrative-tools\" | \"common-illustrative-twitter\" | \"common-illustrative-umbrella\" | \"common-illustrative-upload\" | \"common-illustrative-van\" | \"common-illustrative-washingmachine\" | \"common-illustrative-water_damage\" | \"common-illustrative-wechat\" | \"common-illustrative-wheelchair\" | \"common-illustrative-window_broken\" | \"common-illustrative-windturbine\" | \"common-illustrative-within1hr\" | \"common-illustrative-within24hrs\" | \"common-illustrative-within72hrs\" | \"common-illustrative-wlan\" | \"common-illustrative-woman\" | \"common-illustrative-world\" | \"common-illustrative-youtube\"",
                    "references": {
                        "IconName": {
                            "location": "import",
                            "path": "./Icon.types",
                            "id": "src/components/elements/Icon/Icon.types.ts::IconName"
                        }
                    }
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "sets the name of the icon"
                },
                "getter": false,
                "setter": false,
                "reflect": false
            },
            "size": {
                "type": "string",
                "attribute": "size",
                "mutable": false,
                "complexType": {
                    "original": "IconSize",
                    "resolved": "\"lg\" | \"md\" | \"sm\" | \"xl\" | \"xs\" | undefined",
                    "references": {
                        "IconSize": {
                            "location": "import",
                            "path": "./Icon.types",
                            "id": "src/components/elements/Icon/Icon.types.ts::IconSize"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "sets icon to provided prop size"
                },
                "getter": false,
                "setter": false,
                "reflect": false
            },
            "lazyLoad": {
                "type": "boolean",
                "attribute": "lazy-load",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "defines if the icon is lazy loaded or not (default: false)"
                },
                "getter": false,
                "setter": false,
                "reflect": false,
                "defaultValue": "false"
            },
            "alt": {
                "type": "string",
                "attribute": "alt",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "sets alt text"
                },
                "getter": false,
                "setter": false,
                "reflect": false,
                "defaultValue": "''"
            }
        };
    }
    static get states() {
        return {
            "_svg": {},
            "_loaded": {}
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "alt",
                "methodName": "onAltChange"
            }, {
                "propName": "name",
                "methodName": "onNameChange"
            }, {
                "propName": "lazyLoad",
                "methodName": "lazyLoadChanged"
            }];
    }
}
//# sourceMappingURL=Icon.js.map
